/**
 * Bounded rendering for lists that can grow without limit (OPEN-053).
 *
 * The requestor table is designed for hundreds of people across many operators, and a single
 * person can hold slots on hundreds of locations. Rendering either list in full puts an
 * unbounded number of DOM nodes on screen, which is the "wall of cards" problem.
 *
 * The answer here is a render cap plus an explicit count of what is hidden — NOT virtualization.
 * No package in this monorepo uses a virtualization library, so adding one sets a precedent for
 * a problem the filter already solves: nobody finds a person by scrolling to row 250, they type
 * a name. The cap keeps the DOM bounded at any scale with no dependency, and saying "showing
 * 100 of 342" out loud is what stops a truncated list from being mistaken for a complete one.
 *
 * If a screen ever genuinely needs to scroll through thousands, revisit — but with evidence.
 */

/** Rows rendered at once before the "refine your filter" hint takes over. */
export const RENDER_CAP = 100;

export interface CappedList<T> {
  readonly shown: readonly T[];
  /** How many matched but are not rendered. Zero when the whole list fits. */
  readonly hidden: number;
  /** Total matching the current filter, shown or not. */
  readonly total: number;
}

export function capForRender<T>(items: readonly T[], cap: number = RENDER_CAP): CappedList<T> {
  if (items.length <= cap) return { shown: items, hidden: 0, total: items.length };
  return { shown: items.slice(0, cap), hidden: items.length - cap, total: items.length };
}

/**
 * The sentence shown under a capped list, or null when everything is on screen.
 *
 * Deliberately names both numbers. "Showing 100" alone reads as a complete list of 100.
 */
export function capNotice(list: CappedList<unknown>, noun = 'results'): string | null {
  if (list.hidden === 0) return null;
  return `Showing ${list.shown.length} of ${list.total} ${noun} — refine the filter to narrow further.`;
}
